/** @jsx jsx */
import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { Button, Classes, InputGroup, Tooltip } from '@blueprintjs/core';
import { Dialog } from 'components/dialogs';
import { jsx, css } from '@emotion/react';
import { useSavedStateSelectors } from 'hooks/savedViews/hooks';
import { useIsSavedStateAvailable } from 'hooks/useApplicationState';
import { SavedStatesActionCreator } from 'hooks/savedViews/actions';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { v4 as uuid } from 'uuid';

/**
 * Component responsible for causing the current active explorer to be saved.
 * This should open the SaveCurrentView View modal.
 *
 * @returns {*}
 * @constructor
 */
export const SaveCurrentView = () => {
	const {
		state: { explorer },
		dispatch: workspaceDispatch,
	} = useContext(WorkspaceContext);

	const { explorers, dispatch } = useSavedStateSelectors();
	const isSavable = useIsSavedStateAvailable();

	const [name, setName] = useState('');
	const [isDialogOpen, setDialogState] = useState(false);
	const [fetchSavedExplorer, setFetchSavedExplorer] = useState(false);
	const [id, setId] = useState(null);

	const openSaveModal = () => {
		setName(randName());
		setDialogState(true);
	};

	const closeSaveModal = () => {
		setDialogState(false);
	};

	const saveState = () => {
		if (name !== '') {
			const id = uuid();
			setId(id);
			dispatch(SavedStatesActionCreator.saveExplorer(id, explorer, name));
			setFetchSavedExplorer(true);
		}
	};

	// load the saved meta data into the explorer before closing the dialog
	useEffect(() => {
		if (fetchSavedExplorer) {
			const savedExplorer = explorers.filter((x) => x.metaData.id === id)[0];
			workspaceDispatch(WorkspaceActionCreator.updateMetaData(savedExplorer.metaData));
			closeSaveModal();
		}
	}, [id, fetchSavedExplorer, explorers, workspaceDispatch]);

	if (!isSavable) return null;

	return (
		<Fragment>
			<Tooltip content={'Save as new view'} className={Classes.FIXED}>
				<Button
					icon={'plus'}
					onClick={openSaveModal}
				/>
			</Tooltip>
			<Dialog
				isOpen={isDialogOpen}
				onClose={closeSaveModal}
				title={'Save New View'}
				icon={'plus'}
				body={
					<div className={Classes.DIALOG_BODY}>
						<p>To save this view, enter a title.</p>
						<InputGroup type="text" value={name} onChange={(e) => setName(e.target.value)} fill />
					</div>
				}
				footer={
					<div className={Classes.DIALOG_FOOTER}>
						<Button minimal onClick={closeSaveModal}>
							Cancel
						</Button>
						<Button outlined onClick={saveState} intent="primary">
							Save
						</Button>
					</div>
				}
			/>
		</Fragment>
	);
};

export const SaveExistingView = () => {
	const [isSaveModalOpen, setSaveModalOpen] = useState(false);

	const openSaveModal = () => {
		setSaveModalOpen(true);
	};

	const closeSaveModal = () => {
		setSaveModalOpen(false);
	};

	const {
		state: { explorer, explorer: workspaceExplorer },
	} = useContext(WorkspaceContext);

	const { dispatch } = useSavedStateSelectors();
	const isSavable = useIsSavedStateAvailable();

	const saveState = () => {
		closeSaveModal();
		dispatch(SavedStatesActionCreator.updateView(explorer));
	};

	if (!isSavable) return null;

	if (!workspaceExplorer.metaData) return null;

	return (
		<React.Fragment>
			<Tooltip content={'Update Current'} className={Classes.FIXED}>
				<Button
					icon={'floppy-disk'}
					onClick={openSaveModal}
					// disabled={!workspaceExplorer.metaData}
				/>
			</Tooltip>
			<Dialog
				isOpen={isSaveModalOpen}
				onClose={closeSaveModal}
				title={'Save View'}
				icon="floppy-disk"
				body={
					<div
						className={Classes.DIALOG_BODY}
						css={css`
							display: flex;
						`}
					>
						<p>Saving this will update and overwrite any previously saved filter settings.</p>
					</div>
				}
				footer={
					<div className={Classes.DIALOG_FOOTER}>
						<Button minimal onClick={closeSaveModal}>
							Cancel
						</Button>
						<Button outlined onClick={saveState} intent="primary">
							Save
						</Button>
					</div>
				}
			/>
		</React.Fragment>
	);
};

export function SaveWidget({ card }) {
	const { dispatch } = useSavedStateSelectors();

	const [isDialogOpen, setDialogState] = useState(false);
	const [name, setName] = useState('');

	const openSaveModal = () => {
		setName(randName());
		setDialogState(true);
	};

	const closeSaveModal = () => {
		setDialogState(false);
	};

	const {
		state: {
			explorer: { selectedDefinitionName, filters },
		},
	} = useContext(WorkspaceContext);

	const saveWidget = useCallback(() => {
		if (name) {
			const id = uuid();
			dispatch(SavedStatesActionCreator.saveWidget(id, card, selectedDefinitionName, filters, name));
		}
		closeSaveModal();
	}, [dispatch, card, selectedDefinitionName, filters, name]);

	return (
		<Fragment>
			<Button
				cy-test="save-widget-button"
				icon="floppy-disk"
				onClick={openSaveModal}
				minimal
			/>
			<Dialog
				isOpen={isDialogOpen}
				onClose={closeSaveModal}
				title={'Save Widget View'}
				icon="floppy-disk"
				body={
					<div className={Classes.DIALOG_BODY}>
						<p>To save this widget view, enter a title.</p>
						<InputGroup type="text" value={name} onChange={(e) => setName(e.target.value)} fill />
					</div>
				}
				footer={
					<div className={Classes.DIALOG_FOOTER}>
						<Button
							onClick={closeSaveModal}
							children={'Cancel'}
							css={css`
								margin-right: 0.5rem;
							`}
							minimal
						/>
						<Button onClick={saveWidget} children={'Save'} intent="primary" outlined disabled={name.length === 0} />
					</div>
				}
			/>
		</Fragment>
	);
}

function randName() {
	const adjectives = ['Happy', 'Snappy', 'Quick', 'Quirky'];
	const nouns = ['apple', 'walrus', 'whale', 'robot'];
	const firstIndex = Math.floor(Math.random() * adjectives.length);
	const secondIndex = Math.floor(Math.random() * nouns.length);
	return `${adjectives[firstIndex]} ${nouns[secondIndex]}`;
}
